/**
 * Admin Email Activity section (dashboard page) — drilldown reporting over
 * contact sends: overall KPIs → companies → senders (partner keys, grouped by
 * their rotation-stable Reply-To identity) → recipients → messages, with a
 * message drawer (full content + provenance + raw SendGrid event timeline)
 * and a filtered export of the raw event table.
 *
 * All display date strings are precomputed where rows are BUILT (NG0100 —
 * ui-best-practices §7); view-model rows are rebuilt once per load, never in
 * a bound getter.
 */
import { ChangeDetectorRef, Component, HostListener, NgZone, OnInit } from '@angular/core';
import { LatestRequest } from 'agu_app_shell';
import { AdminService } from '../../../../services/admin.service';
import type {
    MapsContactDeliveryMode,
    MapsEmailActivityFilter,
    MapsEmailActivityGroupBy,
    MapsEmailActivityRow,
    MapsEmailMessageDetail,
    MapsEmailMessageRow,
    MapsEmailTimelinePoint,
} from '../../../../models/maps.types';

type DatePreset = '7d' | '30d' | '90d' | 'all' | 'custom';

/** One load's mapped result, assigned to the view only once the load is still the newest. */
type LoadedView =
    | { Level: 'messages'; Rows: MessageVm[]; Total: number; Timeline: TimelineVm | null }
    | { Level: 'activity'; Rows: ActivityVm[]; Kpis: Kpis; Timeline: TimelineVm | null };

/** One drill step. Level order is fixed: company → sender → recipient. */
interface DrillCrumb {
    level: 'company' | 'sender' | 'recipient';
    key: string;
    label: string;
}

interface ActivityVm extends MapsEmailActivityRow {
    lastActivityText: string;
    openRateText: string;
    clickRateText: string;
}

interface MessageVm extends MapsEmailMessageRow {
    createdAtText: string;
    engagementText: string;
}

interface EventVm {
    id: string;
    eventType: string;
    occurredAtText: string;
    url: string | null;
    reason: string | null;
}

interface DetailVm {
    detail: MapsEmailMessageDetail;
    createdAtText: string;
    sentAtText: string | null;
    events: EventVm[];
    /** "Alice (link: Meridian outreach)" / MJ user name / the bare reply-to. */
    senderText: string;
    bodyLines: string[];
}

/**
 * Engagement timeline chart (delivered/open/click event counts per bucket).
 * All geometry and label strings are precomputed HERE, at load time — the
 * template only binds static values (NG0100). Series colors are a validated
 * 3-hue categorical palette drawn from the MAPS accents (blue/green/orange;
 * dataviz six-checks PASS on the white card surface) — raw hex is permitted
 * for SVG data palettes (ui-best-practices §1).
 */
interface TimelineSeriesVm {
    label: string;
    color: string;
    path: string;
    markers: { x: number; y: number }[];
    endLabelY: number;
    total: number;
}

interface TimelineVm {
    series: TimelineSeriesVm[];
    xTicks: { x: number; label: string }[];
    yTicks: { y: number; label: string }[];
    /** x per bucket, for the hover crosshair. */
    bucketXs: number[];
    tooltips: { title: string; lines: string[] }[];
    weekly: boolean;
}

const TIMELINE_SERIES: readonly { key: 'delivered' | 'opened' | 'clicked'; label: string; color: string }[] = [
    { key: 'delivered', label: 'Delivered', color: '#0b5e8e' },
    { key: 'opened', label: 'Opened', color: '#166534' },
    { key: 'clicked', label: 'Clicked', color: '#c9741f' },
];
// Chart viewBox geometry (viewBox units; the SVG scales to container width).
const CHART_W = 800;
const CHART_H = 220;
const CHART_M = { top: 14, right: 92, bottom: 26, left: 36 };

interface Kpis {
    messages: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    failed: number;
    openRateText: string;
    clickRateText: string;
}

const ALL_MODES: readonly MapsContactDeliveryMode[] = ['dryrun', 'sandbox', 'redirect', 'live'];
const ALL_EVENT_TYPES: readonly string[] = ['processed', 'deferred', 'delivered', 'open', 'click', 'bounce', 'dropped', 'spamreport'];
const EMPTY_KPIS: Kpis = { messages: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0, openRateText: '—', clickRateText: '—' };

@Component({
    selector: 'maps-email-activity',
    standalone: false,
    templateUrl: './email-activity.component.html',
    styleUrl: './email-activity.component.scss',
})
/** The MAPS admin email-activity screen: KPIs and a drilldown from company to sender to recipient to the message list, with message detail (including delivery events) and an events export. */
export class EmailActivityComponent implements OnInit {
    isLoading = true;
    errorMessage: string | null = null;

    // ---- filter toolbar ----
    datePreset: DatePreset = '30d';
    customFrom = '';
    customTo = '';
    /** Selected delivery modes; all four = no server-side mode clause. */
    selectedModes = new Set<MapsContactDeliveryMode>(ALL_MODES);
    readonly allModes = ALL_MODES;

    // ---- drilldown ----
    crumbs: DrillCrumb[] = [];
    activityRows: ActivityVm[] = [];
    kpis: Kpis = EMPTY_KPIS;

    // ---- engagement timeline chart ----
    timeline: TimelineVm | null = null;
    hoverIndex: number | null = null;
    readonly chartW = CHART_W;
    readonly chartH = CHART_H;
    readonly chartM = CHART_M;

    // ---- message level ----
    messageRows: MessageVm[] = [];
    messageTotal = 0;
    messagePage = 1;
    messagePageSize = 20;

    // ---- message drawer ----
    detailVm: DetailVm | null = null;
    isDetailLoading = false;

    /** Latest-wins sequencing: a response whose request was superseded (or cancelled) is dropped. */
    private readonly loadRequest = new LatestRequest<null>();
    private readonly detailRequest = new LatestRequest<string>();

    // ---- export popover ----
    isExportOpen = false;
    isExporting = false;
    exportFormat: 'csv' | 'xlsx' = 'csv';
    selectedEventTypes = new Set<string>(ALL_EVENT_TYPES);
    readonly allEventTypes = ALL_EVENT_TYPES;

    constructor(
        private readonly admin: AdminService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    async ngOnInit(): Promise<void> {
        await this.reload();
    }

    // ------------------------------------------------------------ level state

    /** Messages level sits below the three aggregate grains. */
    get atMessageLevel(): boolean {
        return this.crumbs.length === 3;
    }

    get currentGroupBy(): MapsEmailActivityGroupBy {
        if (this.crumbs.length === 0) return 'COMPANY';
        if (this.crumbs.length === 1) return 'SENDER';
        return 'RECIPIENT';
    }

    /** Column header for the drill grain. */
    get levelLabel(): string {
        if (this.crumbs.length === 0) return 'Company';
        if (this.crumbs.length === 1) return 'Sender';
        return 'Recipient';
    }

    // ------------------------------------------------------------ filters

    SetPreset(preset: DatePreset): void {
        this.datePreset = preset;
        void this.resetAndReload();
    }

    OnCustomDateChange(): void {
        if (this.datePreset === 'custom') {
            void this.resetAndReload();
        }
    }

    ToggleMode(mode: MapsContactDeliveryMode): void {
        if (this.selectedModes.has(mode)) {
            // Never allow an empty mode set — the last chip stays on.
            if (this.selectedModes.size === 1) return;
            this.selectedModes.delete(mode);
        } else {
            this.selectedModes.add(mode);
        }
        void this.resetAndReload();
    }

    IsModeOn(mode: MapsContactDeliveryMode): boolean {
        return this.selectedModes.has(mode);
    }

    /** The filter for the CURRENT drill position (date + modes + crumb keys). */
    private effectiveFilter(): MapsEmailActivityFilter {
        const filter: MapsEmailActivityFilter = {};
        const range = this.dateRange();
        if (range.from) filter.from = range.from;
        if (range.to) filter.to = range.to;
        if (this.selectedModes.size < ALL_MODES.length) {
            filter.deliveryModes = ALL_MODES.filter((m) => this.selectedModes.has(m));
        }
        for (const crumb of this.crumbs) {
            if (crumb.level === 'company') filter.companyId = crumb.key;
            if (crumb.level === 'sender') {
                if (crumb.key === '') filter.senderUnknown = true;
                else filter.senderKey = crumb.key;
            }
            if (crumb.level === 'recipient') filter.recipientEmail = crumb.key;
        }
        return filter;
    }

    private dateRange(): { from: string | null; to: string | null } {
        if (this.datePreset === 'all') return { from: null, to: null };
        if (this.datePreset === 'custom') {
            const from = this.customFrom ? new Date(`${this.customFrom}T00:00:00`) : null;
            const to = this.customTo ? new Date(`${this.customTo}T23:59:59.999`) : null;
            return {
                from: from && !Number.isNaN(from.getTime()) ? from.toISOString() : null,
                to: to && !Number.isNaN(to.getTime()) ? to.toISOString() : null,
            };
        }
        const days = this.datePreset === '7d' ? 7 : this.datePreset === '30d' ? 30 : 90;
        return { from: new Date(Date.now() - days * 24 * 3600 * 1000).toISOString(), to: null };
    }

    // ------------------------------------------------------------ loading

    /** Filter changes restart the drill from the top (a narrower range can empty deep levels). */
    private async resetAndReload(): Promise<void> {
        this.crumbs = [];
        this.messagePage = 1;
        await this.reload();
    }

    private async reload(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;
        // Rows are mapped to view-models inside the fetch, so a mapper throw settles `failed`.
        const outcome = await this.loadRequest.Run(null, async (): Promise<LoadedView> => {
            const filter = this.effectiveFilter();
            if (this.atMessageLevel) {
                const [page, timeline] = await Promise.all([
                    this.admin.EmailMessages(filter, this.messagePageSize, (this.messagePage - 1) * this.messagePageSize),
                    this.admin.EmailTimeline(filter),
                ]);
                return { Level: 'messages', Rows: page.items.map((m) => this.toMessageVm(m)), Total: page.totalCount, Timeline: this.toTimelineVm(timeline) };
            }
            const [rows, timeline] = await Promise.all([
                this.admin.EmailActivity(filter, this.currentGroupBy),
                this.admin.EmailTimeline(filter),
            ]);
            return { Level: 'activity', Rows: rows.map((r) => this.toActivityVm(r)), Kpis: this.computeKpis(rows), Timeline: this.toTimelineVm(timeline) };
        });
        if (outcome.Status === 'stale') return;
        if (outcome.Status === 'failed') {
            // Browser-side instrumentation: MJ logger isn't bundled into the Angular app.
            console.error('[maps.admin] email activity load failed', outcome.Error);
            this.zone.run(() => {
                this.errorMessage = `Could not load email activity: ${outcome.Error.message || 'unknown error'}`;
                this.isLoading = false;
                this.cdr.detectChanges();
            });
            return;
        }
        const view = outcome.Value;
        this.zone.run(() => {
            if (view.Level === 'messages') {
                this.messageRows = view.Rows;
                this.messageTotal = view.Total;
            } else {
                this.activityRows = view.Rows;
                this.kpis = view.Kpis;
            }
            this.timeline = view.Timeline;
            this.hoverIndex = null;
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }

    // ------------------------------------------------------------ timeline chart

    /** Build the chart view-model from fresh points; null when there is nothing to plot. */
    private toTimelineVm(points: MapsEmailTimelinePoint[]): TimelineVm | null {
        const hasEvents = points.some((p) => p.delivered + p.opened + p.clicked > 0);
        return hasEvents ? buildTimelineVm(points) : null;
    }

    OnChartMove(event: MouseEvent): void {
        if (!this.timeline) return;
        const svg = event.currentTarget as SVGSVGElement;
        const rect = svg.getBoundingClientRect();
        // The SVG scales to its container: map client-x back into viewBox units.
        const x = ((event.clientX - rect.left) / rect.width) * CHART_W;
        let nearest = 0;
        let best = Number.POSITIVE_INFINITY;
        this.timeline.bucketXs.forEach((bx, i) => {
            const d = Math.abs(bx - x);
            if (d < best) {
                best = d;
                nearest = i;
            }
        });
        this.hoverIndex = nearest;
    }

    OnChartLeave(): void {
        this.hoverIndex = null;
    }

    /** Tooltip x in PERCENT of chart width so the HTML overlay tracks the scaled SVG. */
    get hoverXPercent(): number {
        if (this.timeline === null || this.hoverIndex === null) return 0;
        return (this.timeline.bucketXs[this.hoverIndex] / CHART_W) * 100;
    }

    private computeKpis(rows: readonly MapsEmailActivityRow[]): Kpis {
        const sum = (pick: (r: MapsEmailActivityRow) => number) => rows.reduce((acc, r) => acc + pick(r), 0);
        const delivered = sum((r) => r.delivered);
        const opened = sum((r) => r.opened);
        const clicked = sum((r) => r.clicked);
        return {
            messages: sum((r) => r.messages),
            sent: sum((r) => r.sent),
            delivered,
            opened,
            clicked,
            bounced: sum((r) => r.bounced),
            failed: sum((r) => r.failed),
            openRateText: rate(opened, delivered),
            clickRateText: rate(clicked, delivered),
        };
    }

    private toActivityVm(row: MapsEmailActivityRow): ActivityVm {
        return {
            ...row,
            lastActivityText: formatDateTime(row.lastActivityAt) ?? '—',
            openRateText: rate(row.opened, row.delivered),
            clickRateText: rate(row.clicked, row.delivered),
        };
    }

    private toMessageVm(row: MapsEmailMessageRow): MessageVm {
        const engagement: string[] = [];
        if (row.openCount > 0) engagement.push(`${row.openCount} open${row.openCount === 1 ? '' : 's'}`);
        if (row.clickCount > 0) engagement.push(`${row.clickCount} click${row.clickCount === 1 ? '' : 's'}`);
        return {
            ...row,
            createdAtText: formatDateTime(row.createdAt) ?? '—',
            engagementText: engagement.length > 0 ? engagement.join(' · ') : '—',
        };
    }

    // ------------------------------------------------------------ drilldown

    DrillInto(row: ActivityVm): void {
        this.crumbs = [...this.crumbs, { level: this.crumbLevelForCurrentGrain(), key: row.key, label: row.label }];
        this.messagePage = 1;
        void this.reload();
    }

    private crumbLevelForCurrentGrain(): DrillCrumb['level'] {
        if (this.crumbs.length === 0) return 'company';
        if (this.crumbs.length === 1) return 'sender';
        return 'recipient';
    }

    /** Breadcrumb: index -1 = the overall root, otherwise keep crumbs up to index. */
    AscendTo(index: number): void {
        this.crumbs = this.crumbs.slice(0, index + 1);
        this.messagePage = 1;
        void this.reload();
    }

    TrackByKey(_index: number, row: ActivityVm): string {
        return row.key;
    }

    TrackByMessageId(_index: number, row: MessageVm): string {
        return row.contactMessageId;
    }

    TrackByCrumb(index: number, crumb: DrillCrumb): string {
        return `${index}:${crumb.key}`;
    }

    // ------------------------------------------------------------ messages page

    OnPageChange(page: number): void {
        this.messagePage = page;
        void this.reload();
    }

    OnPageSizeChange(size: number): void {
        this.messagePageSize = size;
        this.messagePage = 1;
        void this.reload();
    }

    // ------------------------------------------------------------ drawer

    async OpenDetail(row: MessageVm): Promise<void> {
        this.isDetailLoading = true;
        const outcome = await this.detailRequest.Run(row.contactMessageId, (id) => this.admin.EmailMessageDetail(id));
        if (outcome.Status === 'stale') return;
        if (outcome.Status === 'failed') {
            console.error('[maps.admin] email message detail load failed', outcome.Error);
            this.zone.run(() => {
                this.errorMessage = 'Could not load the message detail.';
                this.isDetailLoading = false;
                this.cdr.detectChanges();
            });
            return;
        }
        this.zone.run(() => {
            this.detailVm = outcome.Value ? this.toDetailVm(outcome.Value) : null;
            this.isDetailLoading = false;
            this.cdr.detectChanges();
        });
    }

    CloseDetail(): void {
        // Supersede any in-flight detail request so it cannot reopen the drawer.
        this.detailRequest.Cancel();
        this.isDetailLoading = false;
        this.detailVm = null;
    }

    private toDetailVm(detail: MapsEmailMessageDetail): DetailVm {
        const sender = detail.linkName
            ? `${detail.message.replyTo ?? '(unknown sender)'} · link: ${detail.linkName}`
            : detail.userName
              ? `${detail.message.replyTo ?? '(unknown sender)'} · staff: ${detail.userName}`
              : detail.message.replyTo ?? '(unknown sender)';
        return {
            detail,
            createdAtText: formatDateTime(detail.message.createdAt) ?? '—',
            sentAtText: formatDateTime(detail.message.sentAt),
            senderText: sender,
            bodyLines: detail.body.split('\n'),
            events: detail.events.map((e) => ({
                id: e.id,
                eventType: e.eventType,
                occurredAtText: formatDateTime(e.occurredAt) ?? '—',
                url: e.url,
                reason: e.reason,
            })),
        };
    }

    // ------------------------------------------------------------ export

    ToggleExport(): void {
        this.isExportOpen = !this.isExportOpen;
    }

    /**
     * Toggles one export event type; the last selected type cannot be removed. On that refusal the
     * browser has already cleared the native checkbox, and the `[checked]` binding is still `true`, so
     * Angular never rewrites it — re-check `box` here or the UI shows no type while the export keeps one.
     */
    ToggleEventType(type: string, box: Pick<HTMLInputElement, 'checked'>): void {
        if (this.selectedEventTypes.has(type)) {
            if (this.selectedEventTypes.size === 1) {
                box.checked = true;
                return;
            }
            this.selectedEventTypes.delete(type);
        } else {
            this.selectedEventTypes.add(type);
        }
    }

    IsEventTypeOn(type: string): boolean {
        return this.selectedEventTypes.has(type);
    }

    async RunExport(): Promise<void> {
        if (this.isExporting) return;
        this.isExporting = true;
        this.errorMessage = null;
        try {
            const eventTypes =
                this.selectedEventTypes.size < ALL_EVENT_TYPES.length
                    ? ALL_EVENT_TYPES.filter((t) => this.selectedEventTypes.has(t))
                    : null;
            const result = await this.admin.EmailEventsExport({
                filter: this.effectiveFilter(),
                eventTypes,
                format: this.exportFormat,
            });
            // Same-tab hidden-anchor click: the server bakes
            // `Content-Disposition: attachment` into the blob at upload, so
            // this navigation downloads WITHOUT unloading the SPA. Deliberately
            // no target="_blank" — by the time the awaited mutation resolves
            // the click's user-activation token has expired and popup blockers
            // silently drop new-tab navigations (export-button precedent).
            const anchor = document.createElement('a');
            anchor.href = result.downloadUrl;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            this.zone.run(() => {
                this.isExportOpen = false;
                this.isExporting = false;
                this.cdr.detectChanges();
            });
        } catch (err) {
            console.error('[maps.admin] email events export failed', err);
            this.zone.run(() => {
                this.errorMessage = 'Could not export events.';
                this.isExporting = false;
                this.cdr.detectChanges();
            });
        }
    }

    @HostListener('document:keydown.escape')
    OnDocumentEscape(): void {
        if (this.isExportOpen) {
            this.isExportOpen = false;
            this.cdr.detectChanges();
        }
    }

    @HostListener('document:click', ['$event'])
    OnDocumentClick(event: MouseEvent): void {
        if (!this.isExportOpen) return;
        const target = event.target as HTMLElement | null;
        if (target && !target.closest('.export-anchor')) {
            this.isExportOpen = false;
            this.cdr.detectChanges();
        }
    }
}

/** "72%" when the denominator is real; em-dash otherwise. */
function rate(numerator: number, denominator: number): string {
    if (denominator <= 0) return '—';
    return `${Math.round((numerator / denominator) * 100)}%`;
}

/** "Aug 20, 2026, 11:00 AM" — built at row construction time (NG0100). */
function formatDateTime(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(t));
}

/** "Aug 20" from a 'YYYY-MM-DD' UTC bucket key (formatted in UTC so the day never shifts). */
function formatBucketDate(day: string): string {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

/** All chart geometry + label strings, computed once per load (pure). */
function buildTimelineVm(points: MapsEmailTimelinePoint[]): TimelineVm {
    const n = points.length;
    const plotW = CHART_W - CHART_M.left - CHART_M.right;
    const plotH = CHART_H - CHART_M.top - CHART_M.bottom;
    const xFor = (i: number): number => (n === 1 ? CHART_M.left + plotW / 2 : CHART_M.left + (i * plotW) / (n - 1));

    const maxCount = Math.max(1, ...points.map((p) => Math.max(p.delivered, p.opened, p.clicked)));
    const yStep = Math.max(1, Math.ceil(maxCount / 4));
    const yMax = yStep * Math.ceil(maxCount / yStep);
    const yFor = (v: number): number => CHART_M.top + plotH - (v / yMax) * plotH;

    const series: TimelineSeriesVm[] = TIMELINE_SERIES.map((s) => {
        const markers = points.map((p, i) => ({ x: round1(xFor(i)), y: round1(yFor(p[s.key])) }));
        return {
            label: s.label,
            color: s.color,
            path: markers.map((m, i) => `${i === 0 ? 'M' : 'L'}${m.x},${m.y}`).join(' '),
            markers,
            endLabelY: markers[n - 1].y,
            total: points.reduce((acc, p) => acc + p[s.key], 0),
        };
    });
    // Nudge end labels apart (12 viewBox units min) so Delivered/Opened/Clicked never overlap.
    const byY = [...series].sort((a, b) => a.endLabelY - b.endLabelY);
    for (let i = 1; i < byY.length; i++) {
        if (byY[i].endLabelY - byY[i - 1].endLabelY < 12) {
            byY[i].endLabelY = byY[i - 1].endLabelY + 12;
        }
    }

    const labelEvery = Math.max(1, Math.ceil(n / 7));
    const xTicks = points
        .map((p, i) => ({ i, label: formatBucketDate(p.date) }))
        .filter(({ i }) => i % labelEvery === 0 || i === n - 1)
        .map(({ i, label }) => ({ x: round1(xFor(i)), label }));

    const yTicks: { y: number; label: string }[] = [];
    for (let v = 0; v <= yMax; v += yStep) {
        yTicks.push({ y: round1(yFor(v)), label: String(v) });
    }

    return {
        series,
        xTicks,
        yTicks,
        bucketXs: points.map((_, i) => round1(xFor(i))),
        tooltips: points.map((p) => ({
            title: formatBucketDate(p.date),
            lines: TIMELINE_SERIES.map((s) => `${s.label}: ${p[s.key]}`),
        })),
        weekly: n > 1 && daySpan(points[0].date, points[1].date) > 1,
    };
}

function daySpan(a: string, b: string): number {
    return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / (24 * 3600 * 1000));
}

function round1(v: number): number {
    return Math.round(v * 10) / 10;
}
